type DataLayerEvent = Record<string, unknown>

type GtmWindow = Window & {
  dataLayer?: DataLayerEvent[]
  google_tag_manager?: Record<string, unknown>
}

export type GtmPublicConfig = {
  container_id: string
  consent_mode?: boolean
  context?: DataLayerEvent
}

const STORAGE_KEY = 'bp_marketing_attr'
const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
] as const

const loaded = new Set<string>()

function dl(): DataLayerEvent[] {
  const w = window as GtmWindow
  if (!Array.isArray(w.dataLayer)) w.dataLayer = []
  return w.dataLayer
}

export function pushDataLayer(event: string, extra: DataLayerEvent = {}): void {
  dl().push({ event, ...extra })
}

function loadAttribution(): Record<string, string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, string>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/** Grava UTMs/gclid/fbclid da URL na chegada (mesma chave do script clássico). */
export function captureAttribution(): Record<string, string> {
  const stored = loadAttribution()
  const fromUrl: Record<string, string> = {}
  const params = new URLSearchParams(window.location.search)
  for (const key of TRACKED_PARAMS) {
    const val = params.get(key)
    if (val) fromUrl[key] = val
  }
  const merged = { ...stored, ...fromUrl }
  if (!merged.landing_page) {
    merged.landing_page = window.location.href.split('#')[0]
  }
  if (!merged.referrer_url && document.referrer) {
    merged.referrer_url = document.referrer
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  } catch {
    /* ignore */
  }
  return merged
}

/** Tira nome e telefone da query antes do PageView do GTM. */
export function stripPiiFromUrl(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('nome') && !url.searchParams.has('tel')) return
  url.searchParams.delete('nome')
  url.searchParams.delete('tel')
  const next = url.pathname + url.search + url.hash
  window.history.replaceState({}, '', next)
}

/** Carrega o container GTM da campanha e empurra basepolitica_context uma vez. */
export function ensureGtm(gtm: GtmPublicConfig | null | undefined): void {
  if (!gtm?.container_id || !/^GTM-[A-Z0-9]+$/.test(gtm.container_id)) return
  const id = gtm.container_id

  if (gtm.context && !loaded.has(`ctx:${id}`)) {
    dl().push(gtm.context)
    if (gtm.consent_mode) {
      dl().push({ event: 'consent_default', consent_mode: true })
    }
    loaded.add(`ctx:${id}`)
  }

  if (loaded.has(`js:${id}`)) return
  loaded.add(`js:${id}`)

  dl().push({ 'gtm.start': Date.now(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`
  document.head.appendChild(script)
}

/** Conversão de cadastro (SPA não recarrega a página — o gatilho de form do GTM não dispara sozinho). */
export function fireGenerateLead(leadSource = 'convite'): void {
  const utm = captureAttribution()
  pushDataLayer('generate_lead', {
    lead_source: leadSource,
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    utm_content: utm.utm_content || '',
    utm_term: utm.utm_term || '',
    gclid: utm.gclid || '',
    fbclid: utm.fbclid || '',
  })
}
